import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Target, Layers, Rocket, Radio } from 'lucide-react';
import { api } from '../api/client';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@databro/ui';

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check className="mr-1.5 size-3.5" /> : <Copy className="mr-1.5 size-3.5" />}
      {label}
    </Button>
  );
}

function Json({ value }: { value: unknown }) {
  return (
    <pre className="max-h-[400px] overflow-auto rounded-md bg-muted p-3 text-xs">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export default function RecordingDetail() {
  const { id = '' } = useParams();
  const [focus, setFocus] = useState('');
  const [mergePath, setMergePath] = useState('data.items');
  const [dedupeBy, setDedupeBy] = useState('itemid');

  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const recordingQuery = useQuery({
    queryKey: ['recording', id],
    queryFn: () => api.getRecording(id),
    refetchInterval: (q) => (q.state.data?.status === 'recording' ? 2000 : false),
  });

  const responsesQuery = useQuery({
    queryKey: ['recording', id, 'responses', focus],
    queryFn: () => api.getRecordingResponses(id, { focus: focus || undefined, limit: 100 }),
  });

  const mergeMutation = useMutation({
    mutationFn: () => api.getRecordingMerged(id, { path: mergePath, dedupeBy: dedupeBy || undefined, focus: focus || undefined }),
  });

  const promoteMutation = useMutation({
    mutationFn: (body: Parameters<typeof api.promoteRecording>[1]) => api.promoteRecording(id, body),
  });

  const rec = recordingQuery.data;

  if (recordingQuery.isLoading) {
    return <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>;
  }
  if (!rec) {
    return <div className="p-8 text-center text-sm text-muted-foreground">Recording not found.</div>;
  }

  const responsesUrl = `${origin}/api/recordings/${id}/responses${focus ? `?focus=${encodeURIComponent(focus)}` : ''}`;
  const mergedUrl = `${origin}/api/recordings/${id}/merged?path=${encodeURIComponent(mergePath)}${dedupeBy ? `&dedupeBy=${encodeURIComponent(dedupeBy)}` : ''}${focus ? `&focus=${encodeURIComponent(focus)}` : ''}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="icon">
          <Link to="/recordings">
            <ArrowLeft className="size-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            {rec.name}
            {rec.status === 'recording' && (
              <span className="flex items-center gap-1 text-sm font-medium text-red-500">
                <Radio className="size-4 animate-pulse" /> live
              </span>
            )}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {rec.matchCount} responses · started {new Date(rec.startedAt).toLocaleString()}
            {rec.template && <> · recipe: {rec.template.name}</>}
          </p>
        </div>
        <Badge variant={rec.status === 'failed' ? 'destructive' : 'secondary'} className="capitalize">
          {rec.status}
        </Badge>
      </div>

      {/* Endpoints */}
      {rec.endpoints.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Endpoints captured</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {rec.endpoints.map((e) => (
              <div key={`${e.method} ${e.path}`} className="flex items-center gap-2 text-sm">
                <Badge variant="outline" className="font-mono text-[10px]">
                  {e.method}
                </Badge>
                <span className="flex-1 truncate font-mono text-xs" title={e.path}>
                  {e.path}
                </span>
                <span className="tabular-nums text-muted-foreground">{e.hits}</span>
                <button
                  className="text-muted-foreground hover:text-primary"
                  title="Focus this endpoint"
                  onClick={() => setFocus(`*${e.path.split('/').filter(Boolean).pop()}*`)}
                >
                  <Target className="size-3.5" />
                </button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Focus filter */}
      <div className="flex items-end gap-2">
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="focus" className="text-xs">
            Focus filter (URL glob)
          </Label>
          <Input
            id="focus"
            placeholder="blank = all · e.g. *rcmd_items*"
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
            className="font-mono text-xs"
          />
        </div>
        {focus && (
          <Button variant="ghost" size="sm" onClick={() => setFocus('')}>
            Clear
          </Button>
        )}
      </div>

      <Tabs defaultValue="responses">
        <TabsList>
          <TabsTrigger value="responses">Responses</TabsTrigger>
          <TabsTrigger value="merged">
            <Layers className="mr-1.5 size-3.5" /> Merged
          </TabsTrigger>
          <TabsTrigger value="promote">
            <Rocket className="mr-1.5 size-3.5" /> Promote
          </TabsTrigger>
        </TabsList>

        {/* Responses */}
        <TabsContent value="responses" className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {responsesQuery.data?.pagination.total ?? 0} matching responses
            </p>
            <CopyButton text={responsesUrl} label="Copy API URL" />
          </div>
          {(responsesQuery.data?.data ?? []).map((r) => (
            <Card key={r.seq}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-xs font-normal">
                  <Badge variant="outline" className="font-mono text-[10px]">
                    #{r.seq} {r.method} {r.status}
                  </Badge>
                  <span className="truncate font-mono text-muted-foreground" title={r.url}>
                    {r.url}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Json value={r.response} />
              </CardContent>
            </Card>
          ))}
          {responsesQuery.data?.data.length === 0 && (
            <p className="p-8 text-center text-sm text-muted-foreground">No responses match.</p>
          )}
        </TabsContent>

        {/* Merged */}
        <TabsContent value="merged" className="space-y-3">
          <Card>
            <CardContent className="space-y-3 pt-6">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="path" className="text-xs">
                    JSON path
                  </Label>
                  <Input id="path" value={mergePath} onChange={(e) => setMergePath(e.target.value)} className="font-mono text-xs" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dedupe" className="text-xs">
                    Dedupe by (optional)
                  </Label>
                  <Input id="dedupe" value={dedupeBy} onChange={(e) => setDedupeBy(e.target.value)} className="font-mono text-xs" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={() => mergeMutation.mutate()} disabled={mergeMutation.isPending}>
                  <Layers className="mr-1.5 size-4" />
                  Merge
                </Button>
                <CopyButton text={mergedUrl} label="Copy API URL" />
                {mergeMutation.data && (
                  <span className="text-sm text-muted-foreground">
                    {mergeMutation.data.count} items
                  </span>
                )}
              </div>
              {mergeMutation.data && <Json value={mergeMutation.data.items} />}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Promote */}
        <TabsContent value="promote">
          <PromoteForm
            defaultName={rec.name}
            onSubmit={(body) => promoteMutation.mutate(body)}
            pending={promoteMutation.isPending}
            result={promoteMutation.data}
            error={promoteMutation.error as Error | null}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PromoteForm({
  defaultName,
  onSubmit,
  pending,
  result,
  error,
}: {
  defaultName: string;
  onSubmit: (body: Parameters<typeof api.promoteRecording>[1]) => void;
  pending: boolean;
  result?: { actionId: string; slug: string };
  error: Error | null;
}) {
  const [slug, setSlug] = useState(
    defaultName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'recording-action'
  );
  const [name, setName] = useState(defaultName);
  const [platform, setPlatform] = useState<'shopee' | 'tiktok' | 'custom'>('custom');
  const [withPagination, setWithPagination] = useState(true);
  const [paramName, setParamName] = useState('offset');
  const [limit, setLimit] = useState(30);
  const [maxPages, setMaxPages] = useState(50);

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <p className="text-sm text-muted-foreground">
          Turn this recording into a replayable Action. It reuses the captured request credentials —
          the server will replay it (falling back to the browser tier if the site blocks replay).
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="p-slug" className="text-xs">Slug</Label>
            <Input id="p-slug" value={slug} onChange={(e) => setSlug(e.target.value)} className="font-mono text-xs" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-name" className="text-xs">Name</Label>
            <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-platform" className="text-xs">Platform</Label>
            <select
              id="p-platform"
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              value={platform}
              onChange={(e) => setPlatform(e.target.value as 'shopee' | 'tiktok' | 'custom')}
            >
              <option value="shopee">shopee</option>
              <option value="tiktok">tiktok</option>
              <option value="custom">custom</option>
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={withPagination} onChange={(e) => setWithPagination(e.target.checked)} />
          Attach offset pagination
        </label>
        {withPagination && (
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Param name</Label>
              <Input value={paramName} onChange={(e) => setParamName(e.target.value)} className="font-mono text-xs" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Page size</Label>
              <Input type="number" value={limit} onChange={(e) => setLimit(Number(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Max pages</Label>
              <Input type="number" value={maxPages} onChange={(e) => setMaxPages(Number(e.target.value))} />
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button
            onClick={() =>
              onSubmit({
                slug,
                name,
                platform,
                ...(withPagination
                  ? { pagination: { type: 'offset', paramName, limit, maxPages } }
                  : {}),
              })
            }
            disabled={pending}
          >
            <Rocket className="mr-1.5 size-4" />
            Create Action
          </Button>
          {result && (
            <Link to={`/actions/${result.actionId}`} className="text-sm text-primary hover:underline">
              Created "{result.slug}" — open Action →
            </Link>
          )}
          {error && <span className="text-sm text-destructive">{error.message}</span>}
        </div>
      </CardContent>
    </Card>
  );
}
